import { useCallback, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { SelectItemOptionsType } from 'primereact/selectitem';
import { Toolbar } from 'primereact/toolbar';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { AuthenticationType } from '@/app/slices/authentication';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  useCreateFertilization,
  useFertCommentsQuery,
  useFertTechsQuery,
  useTravelingTechsQuery,
} from '@/services/fertilization/hooks';
import { runNewWorkflow, WORKFLOW } from '@/utils/workflow';

import { getSemenMissingRsMoflo } from './rsMofloValidation';
import { CreateFertilizationPayload, PulledSemenType } from './types';
import { useFertilizationContext } from './useFertilizationContext';

type Props = {
  className?: string;
};

export const FertilizationDonorToolbar = ({ className }: Props) => {
  const { t } = useTranslation();
  const me = useSelector(
    (state: { authentication: AuthenticationType }) => state.authentication.me,
  );
  const environmentId = me?.environmentId || 0;
  const dispatch = useDispatch();
  const [travelingTech, setTravelingTech] = useState(false);
  const {
    poolSires,
    procTech,
    setProcTech,
    moveTech,
    setMoveTech,
    fertTech,
    setFertTech,
    fertComments,
    setFertComments,
    selectedSemenRows,
    selectedDonorRows,
    donorGridRef,
    semenGridRef,
    serum,
    cultureIncubatorNo,
    fertPlateLotNo,
    SOFMediaLotNo,
    fertMediaLotNo,
    QCIsolate,
    culturePlateLotNo,
    mineralOilLot,
    EHMMedia,
    handleRefresh,
  } = useFertilizationContext();

  const [params] = useSearchParams();
  const fertilizationSite = params.get('site');
  const fertilizationDate = params.get('date');

  const travelingTechsRes = useTravelingTechsQuery();
  const travelingTechOptions = travelingTechsRes.data;

  const { mutateAsync: createFertilization, isLoading: isUpdating } =
    useCreateFertilization();

  const fertTechsRes = useFertTechsQuery({
    fertSiteId: fertilizationSite,
  });

  const techOptions = fertTechsRes.data;

  const fertCommentsRes = useFertCommentsQuery();
  const fertCommentsOptions = fertCommentsRes.data;

  const semenMissingMoflo = useMemo(
    () => getSemenMissingRsMoflo(selectedSemenRows),
    [selectedSemenRows],
  );

  const tooltipMessage = useMemo(() => {
    let message = 'Please fill in the following values:\n';

    if (!procTech) message += 'Proc Tech,\n';
    if (!moveTech) message += 'Move Tech,\n';
    if (!fertTech) message += 'Fert Tech,\n';

    if (donorGridRef.current && donorGridRef.current.api) {
      const currentSelection = selectedDonorRows || [];
      if (currentSelection)
        for (const donor of currentSelection) {
          if (
            !donor.numFerts ||
            donor.numFerts === '' ||
            donor.numFerts === '0'
          ) {
            message += donor.donorName + ' # in Fert ,\n';
          }
          if (!donor.time || donor.time === '' || donor.time === '0') {
            message += donor.donorName + ' Time,\n';
          }
        }
    }

    if (!fertPlateLotNo) message += 'QC Fert Plate (Lot#),\n';
    if (!SOFMediaLotNo) message += 'QC IVC (Expiration Date),\n';
    if (!fertMediaLotNo) message += 'QC Fert Media (Expiration Date),\n';
    if (!QCIsolate) message += 'QC Isolate/SS+ (Expiration Date),\n';
    if (!culturePlateLotNo) message += 'QC Culture Plates (Lot#),\n';
    if (!mineralOilLot) message += 'QC Mineral Oil (Lot#),\n';
    if (!EHMMedia) message += 'QC EHM (Expiration Date)\n';

    for (const semen of semenMissingMoflo) {
      message += semen.sireName + ' Moflo #,\n';
    }

    return message;
  }, [
    procTech,
    moveTech,
    fertTech,
    fertPlateLotNo,
    SOFMediaLotNo,
    fertMediaLotNo,
    QCIsolate,
    culturePlateLotNo,
    mineralOilLot,
    EHMMedia,
    donorGridRef,
    selectedDonorRows,
    semenMissingMoflo,
  ]);

  const isLocked = useMemo(() => {
    let lock = false;

    if (selectedSemenRows?.length === 0) return (lock = true);

    if (semenMissingMoflo.length > 0) return true;

    if (
      !procTech ||
      !moveTech ||
      !fertTech ||
      !fertPlateLotNo ||
      !SOFMediaLotNo ||
      !fertMediaLotNo ||
      !QCIsolate ||
      !culturePlateLotNo ||
      !mineralOilLot ||
      !EHMMedia
    ) {
      lock = true;
    } else {
      const currentSelection = selectedDonorRows || [];
      if (currentSelection.length === 0) {
        lock = true;
      } else {
        for (const donor of currentSelection) {
          if (
            !donor.numFerts ||
            donor.numFerts === '' ||
            donor.numFerts === '0' ||
            !donor.time ||
            donor.time === '' ||
            donor.time === '0'
          ) {
            lock = true;
            break;
          }
        }
      }
    }

    return lock;
  }, [
    selectedSemenRows,
    procTech,
    moveTech,
    fertTech,
    fertPlateLotNo,
    SOFMediaLotNo,
    fertMediaLotNo,
    QCIsolate,
    culturePlateLotNo,
    mineralOilLot,
    EHMMedia,
    selectedDonorRows,
    semenMissingMoflo,
  ]);

  const handleCreateFert = useCallback(async () => {
    donorGridRef?.current?.api.showLoadingOverlay();
    semenGridRef?.current?.api.showLoadingOverlay();
    const semenUseList: string[] = [];
    const semenTypeList: string[] = [];
    const semenQtyUseList: number[] = [];
    const mofloList: string[] = [];
    const DSList: string[] = [];
    const ivfNumList: number[] = [];
    const fertTimes: string[] = [];
    const fertCommentsList: string[] = [];
    const gridFertCommentsList: number[][] = [];
    const contaminationList: boolean[] = [];
    const currSemen = new Set<PulledSemenType>();

    selectedSemenRows?.forEach((item) => {
      if (!currSemen.has(item)) {
        currSemen.add(item);
        semenUseList.push(item.semenID);
        semenTypeList.push(item.semenTypeName);
        semenQtyUseList.push(parseInt(item.use as string, 10));
      }
    });
    const currentProcTech = procTech;
    const currentMoveTech = moveTech;
    const currentFertTech = fertTech;

    const donorList = donorGridRef.current?.api.getSelectedRows();
    if (donorList && donorList.length > 0) {
      for (const donor of donorList) {
        DSList.push(donor.donorScheduleID);
        fertTimes.push(donor.time || '');
        ivfNumList.push(parseInt(donor.numFerts as string, 10));
        gridFertCommentsList.push(donor.comments ? donor.comments : []);
        fertCommentsList.push(donor.fertComment ? donor.fertComment : '');
        contaminationList.push(donor.contamination ?? false);
        for (const semen of currSemen!) {
          if (donor.semenID === semen.semenID) {
            if (
              ((donor.semenTypeName === 'RS M' ||
                donor.semenTypeName === 'RS F') &&
                (semen.moflo === null || semen.moflo === undefined)) ||
              semen.moflo === ''
            ) {
              dispatch(
                addToast({
                  severity: 'error',
                  summary: t('missingMoflo.missingMoflo'),
                  detail: t('missingMoflo.pulledRsSemenMissingMoflo'),
                  life: ToastLife.LONG,
                }),
              );
              donorGridRef?.current?.api.hideOverlay();
              semenGridRef?.current?.api.hideOverlay();
              throw new Error('Missing Moflo #');
            } else {
              if (semen.moflo) {
                mofloList.push(semen.moflo as string);
              } else {
                mofloList.push('');
              }
            }
          }
        }
      }

      const fertsRes = await createFertilization({
        semenID: semenUseList,
        dsid: DSList,
        semenQtyUse: semenQtyUseList,
        semenTypeList: semenTypeList,
        mofloList: mofloList,
        numFerts: donorList.length,
        fertTime: fertTimes,
        donorScheduleDate: format(
          new Date(fertilizationDate as string),
          'yyyy-MM-dd',
        ),
        fertDate: format(new Date(fertilizationDate as string), 'yyyy-MM-dd'),
        procTechID: procTech as unknown as string,
        moveTechID: moveTech as unknown as string,
        fertTechID: fertTech as unknown as string,
        fertComment: fertCommentsList,
        ivfNum: ivfNumList,
        billUnits: 0,
        pooledSires: poolSires,
        fertCommentList: fertComments,
        sofDate: SOFMediaLotNo ? format(SOFMediaLotNo, 'yyyy-MM-dd') : '',
        fertMediaLotNo: fertMediaLotNo
          ? format(fertMediaLotNo, 'yyyy-MM-dd')
          : '',
        gridFertCommentsDDL: gridFertCommentsList,
        mineralOilLot,
        serum,
        fertPlate: fertPlateLotNo,
        culturePlateLotNo,
        cultureIncubatorNo,
        ehmMedia: EHMMedia ? format(EHMMedia, 'yyyy-MM-dd') : '',
        qcIsolate: QCIsolate,
        isContaminated: contaminationList,
      });

      const payload: CreateFertilizationPayload | undefined = fertsRes;

      if (!payload) {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('payload.unexpectedResponse'),
            detail: t('payload.noPayloadReturned'),
            life: ToastLife.LONG,
          }),
        );
        return;
      }

      const { success, code, message, fertilizationIds } = payload;

      dispatch(
        addToast({
          severity: success ? 'success' : 'error',
          summary: success
            ? t('fertilization.fertilizationCreated')
            : `${t('fertilization.fertilizationFailed')} (${code ?? '—'})`,
          detail:
            message ??
            (success
              ? t('fertilization.fertilizationCreatedSuccessfully')
              : t('fertilization.failedToCreateFertilization')),
          life: success ? ToastLife.DEFAULT : ToastLife.LONG,
        }),
      );

      if (success && (fertilizationIds?.length ?? 0) > 0) {
        try {
          const recordIds = fertilizationIds!.map((id) => ({ RecordId: id }));
          await runNewWorkflow(recordIds, WORKFLOW.DonorProcedure.id);

          dispatch(
            addToast({
              severity: 'success',
              summary: t('workflow.workflowExecuted'),
              detail: `${WORKFLOW.DonorProcedure.label} ${t('workflow.workflowCompletedSuccessfully')}`,
              life: ToastLife.DEFAULT,
            }),
          );
        } catch (error) {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('workflow.workflowError'),
              detail: `${t('workflow.failedToExecuteWorkflow')} ${WORKFLOW.DonorProcedure.label}`,
              life: ToastLife.LONG,
            }),
          );
          console.error('Workflow Error:', error);
        }
        handleRefresh();
      }
    }
    donorGridRef?.current?.api.hideOverlay();
    semenGridRef?.current?.api.hideOverlay();
    setProcTech(currentProcTech);
    setMoveTech(currentMoveTech);
    setFertTech(currentFertTech);
  }, [
    donorGridRef,
    semenGridRef,
    selectedSemenRows,
    procTech,
    moveTech,
    fertTech,
    setProcTech,
    setMoveTech,
    setFertTech,
    createFertilization,
    fertilizationDate,
    poolSires,
    fertComments,
    SOFMediaLotNo,
    fertMediaLotNo,
    mineralOilLot,
    serum,
    fertPlateLotNo,
    culturePlateLotNo,
    cultureIncubatorNo,
    EHMMedia,
    QCIsolate,
    dispatch,
    handleRefresh,
    environmentId,
  ]);
  return (
    <Toolbar
      className={`fertilization__donor-toolbar toolbar-above-panel ${className}`.trim()}
      start={
        <div className="donor-toolbar__actions">
          <Dropdown
            className="donor-toolbar__actions__tech_dropdown"
            placeholder={t('tech.procTech')}
            value={procTech}
            tabIndex={procTech ? -1 : 1}
            onChange={(e) => setProcTech(e.value)}
            options={
              travelingTech
                ? (travelingTechOptions as SelectItemOptionsType)
                : (techOptions as SelectItemOptionsType)
            }
            optionLabel="name"
          />
          <Dropdown
            className="donor-toolbar__actions__tech_dropdown"
            placeholder={t('tech.moveTech')}
            value={moveTech}
            tabIndex={moveTech ? -1 : 2}
            onChange={(e) => setMoveTech(e.value)}
            options={
              travelingTech
                ? (travelingTechOptions as SelectItemOptionsType)
                : (techOptions as SelectItemOptionsType)
            }
            optionLabel="name"
          />
          <Dropdown
            className="donor-toolbar__actions__tech_dropdown"
            placeholder={t('fertilization.fertTech')}
            value={fertTech}
            tabIndex={fertTech ? -1 : 3}
            onChange={(e) => setFertTech(e.value)}
            options={
              travelingTech
                ? (travelingTechOptions as SelectItemOptionsType)
                : (techOptions as SelectItemOptionsType)
            }
            optionLabel="name"
          />
          <MultiSelect
            className="donor-toolbar__actions__comment_dropdown"
            placeholder={t('selectComment.selectComments')}
            value={fertComments}
            options={fertCommentsOptions as SelectItemOptionsType}
            optionLabel="text"
            onChange={(e) => setFertComments(e.value)}
            maxSelectedLabels={1}
          />
          <label className="config-input">{t('tech.travelingTech')}:</label>
          <Checkbox
            onChange={(e) => setTravelingTech(e.checked as boolean)}
            checked={travelingTech}
          />
        </div>
      }
      end={
        <Button
          loading={isUpdating}
          label={t('fertilization.createFert')}
          disabled={isLocked || isUpdating}
          onClick={handleCreateFert}
          tooltip={isLocked ? tooltipMessage : ''}
          tooltipOptions={{ position: 'left', showOnDisabled: true }}
        />
      }
    />
  );
};
